var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable no-constant-condition */
import EJSON from "ejson";
import { EventEmitter } from "events";
import React from "react";
export var TransportStatus;
(function (TransportStatus) {
    TransportStatus["disconnected"] = "disconnected";
    TransportStatus["connecting"] = "connecting";
    TransportStatus["connected"] = "connected";
})(TransportStatus || (TransportStatus = {}));
export class TransportBase {
    constructor(baseURL) {
        this.emitter = new EventEmitter();
        this.lastPing = new Date();
        this.status = TransportStatus.disconnected;
        this.connectedAt = null;
        this.failureCount = 0;
        this.reconnectPending = false;
        this.terminated = false;
        this.lastReconnectAttempt = null;
        this.stabilityCheckInterval = null;
        this.emit = {
            statusChange: (status) => {
                if (this.terminated)
                    return;
                this.emitter.emit("statusChange", status);
            }
        };
        this.on = {
            statusChange: (cb) => {
                this.emitter.on("statusChange", cb);
            }
        };
        this.off = {
            statusChange: (cb) => {
                this.emitter.off("statusChange", cb);
            }
        };
        this.updateStatus = (status) => {
            if (this.terminated)
                return;
            this.v(`Updating status to: ${status}`);
            if (status === TransportStatus.connected) {
                // Clear the outbox before emitting status to prevent calls made as side-effects of status change getting cleared
                this.client.clearOutboxBeforeReconnection();
            }
            this.status = status;
            this.emit.statusChange(status);
            if (status === TransportStatus.connected) {
                this.connectedAt = new Date();
                this.failureCount = 0;
                void this.client.handleOutboxFlushRequest();
            }
        };
        this.pinged = () => {
            this.lastPing = new Date();
        };
        this.handleIncomingMessage = (incoming) => {
            this.pinged();
            const data = EJSON.parse(incoming);
            this.client.incomingMessageEmitter.emit(data.event, data.data);
        };
        this.requestReconnect = () => __awaiter(this, void 0, void 0, function* () {
            if (this.reconnectPending === true) {
                if (this.lastReconnectAttempt != null && this.lastReconnectAttempt < new Date(Date.now() - 30000)) {
                    this.v("Reconnect has been pending for over 30 seconds, going to try reconnecting again.");
                }
                else {
                    return;
                }
            }
            this.reconnectPending = true;
            this.lastReconnectAttempt = new Date();
            while (true) {
                try {
                    const { data } = yield this.client.axios.get(`${this.baseURL}/api/status`);
                    if (data === "OK") {
                        break;
                    }
                }
                catch (_a) {
                    // Ignore errors
                }
                finally {
                    yield new Promise((resolve) => setTimeout(resolve, 500));
                }
            }
            this.v("Attempting to reconnect...");
            void this.connect();
        });
        this.handleConnectionFailure = (reason) => {
            this.v("Connection failure detected: " + reason);
            this.failureCount++;
            if (this.status !== TransportStatus.disconnected) {
                this.disconnect(3900, "connection-failure" + reason);
            }
            void this.requestReconnect();
        };
        this.stopStabilityCheckInterval = () => {
            if (this.stabilityCheckInterval) {
                clearInterval(this.stabilityCheckInterval);
                this.stabilityCheckInterval = null;
            }
        };
        this.kickoffStabilityCheckInterval = () => {
            this.v("Kicking off stability check interval...");
            if (this.stabilityCheckInterval) {
                this.v("Clearing existing stability check interval...");
                clearInterval(this.stabilityCheckInterval);
            }
            this.stabilityCheckInterval = setInterval(() => {
                if (this.lastPing.valueOf() < (Date.now() - 10000)) {
                    this.v(`Connection is unstable, last ping was ${Date.now() - this.lastPing.valueOf()}ms ago, declaring failure.`);
                    this.handleConnectionFailure("stability-check");
                }
                else {
                    this.v(`Connection is stable, last ping was ${Date.now() - this.lastPing.valueOf()}ms ago.`);
                }
            }, 5000);
        };
        this.awaitConnected = () => {
            return new Promise((resolve) => {
                if (this.status === TransportStatus.connected) {
                    resolve();
                }
                else {
                    const handler = (status) => {
                        if (status === TransportStatus.connected) {
                            resolve();
                            this.off.statusChange(handler);
                        }
                    };
                    this.on.statusChange(handler);
                }
            });
        };
        this.useStatus = () => {
            const [v, s] = React.useState(this.status);
            React.useEffect(() => {
                const onStatusChange = (status) => s(status);
                this.on.statusChange(onStatusChange);
                return () => {
                    this.off.statusChange(onStatusChange);
                };
            }, []);
            return v;
        };
        this.useIsConnected = () => {
            return this.useStatus() === TransportStatus.connected;
        };
        this.baseURL = baseURL;
    }
}
